<template>
  <div class="max-w-9xl mx-auto p-6">
    <div
      class="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
    >
      <div>
        <h1 class="text-3xl font-bold text-gray-800">Departments Manager</h1>
        <p class="text-sm text-gray-500 mt-1">
          Departments are normally synchronized from Infoauto. You can add one
          manually and edit the existing ones.
        </p>
      </div>
      <button
        class="shrink-0 px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        @click="openCreate"
      >
        <i class="fas fa-plus mr-1"></i> Add department
      </button>
    </div>

    <!-- Search -->
    <div class="mb-6 bg-white shadow-sm border border-gray-200 rounded-xl p-6">
      <label class="block text-sm font-medium text-gray-700 mb-2">Search</label>
      <div class="relative">
        <input
          v-model="search"
          type="text"
          placeholder="Filter by name, code or department id..."
          class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
        <i
          class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
        ></i>
      </div>
      <div v-if="!loading" class="mt-4 text-sm text-gray-600">
        Showing {{ filteredDepartments.length }} of
        {{ departments.length }} departments
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-8">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
      ></div>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700"
    >
      {{ error }}
      <button class="ml-3 underline" @click="loadDepartments">Retry</button>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!filteredDepartments.length"
      class="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500"
    >
      No departments found.
    </div>

    <!-- List -->
    <div v-else class="space-y-4">
      <div
        v-for="department in filteredDepartments"
        :key="department.id"
        class="bg-white shadow-sm border border-gray-200 rounded-xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div class="min-w-0">
          <div class="flex items-center flex-wrap gap-2">
            <span class="text-lg font-semibold text-gray-900">
              {{ department.nombre }}
            </span>
            <span
              class="px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-700 rounded"
            >
              {{ department.codigo }}
            </span>
            <span
              v-if="isTrue(department.main)"
              class="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded"
            >
              Main
            </span>
            <span
              v-if="isTrue(department.enabledSelfService)"
              class="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded"
            >
              Self service
            </span>
            <span
              v-if="department.hasServerPassword"
              class="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded"
            >
              <i class="fas fa-lock mr-1"></i>Password saved
            </span>
          </div>
          <div class="mt-1 text-sm text-gray-500 truncate">
            <span class="font-mono">{{ department.departamentoId }}</span>
            <span v-if="department.telefono"> · {{ department.telefono }}</span>
            <span v-if="department.direccion">
              · {{ department.direccion }}</span
            >
          </div>
        </div>
        <div class="shrink-0">
          <button
            class="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            @click="openEdit(department)"
          >
            Edit
          </button>
        </div>
      </div>
    </div>

    <!-- Create / edit modal -->
    <BaseModal v-model="showForm" class="department-form-modal">
      <template #header>
        <h2 class="text-lg font-semibold">
          {{ isCreating ? "Add department" : "Edit department" }}
        </h2>
      </template>

      <form class="p-1" @submit.prevent="submitForm">
        <!-- Identifiers -->
        <h3 class="text-sm font-semibold text-gray-900 uppercase mb-3">
          Identifiers
        </h3>
        <p v-if="!isCreating" class="text-xs text-gray-500 mb-3">
          These identifiers come from Infoauto and cannot be changed.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Company id <span v-if="isCreating" class="text-red-500">*</span>
            </label>
            <input
              v-model="form.ciaId"
              type="text"
              maxlength="10"
              :disabled="!isCreating"
              :required="isCreating"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Department id
              <span v-if="isCreating" class="text-red-500">*</span>
            </label>
            <input
              v-model="form.departamentoId"
              type="text"
              maxlength="20"
              :disabled="!isCreating"
              :required="isCreating"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Code <span v-if="isCreating" class="text-red-500">*</span>
            </label>
            <input
              v-model="form.codigo"
              type="text"
              maxlength="20"
              :disabled="!isCreating"
              :required="isCreating"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            />
          </div>
        </div>

        <!-- General -->
        <h3 class="text-sm font-semibold text-gray-900 uppercase mb-3">
          General
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.nombre"
              type="text"
              required
              maxlength="100"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              v-model="form.telefono"
              type="tel"
              maxlength="50"
              placeholder="+1 305 000 0000"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              v-model="form.direccion"
              type="text"
              maxlength="255"
              placeholder="Street, city, state, country"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <!-- Options -->
        <h3 class="text-sm font-semibold text-gray-900 uppercase mb-3">
          Options
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div class="flex items-start gap-2">
            <input
              id="department-main"
              v-model="form.main"
              type="checkbox"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label for="department-main" class="text-sm text-gray-700">
              Main department
              <span class="block text-xs text-gray-500">
                Only one department can be the main one.
              </span>
            </label>
          </div>
          <div class="flex items-start gap-2">
            <input
              id="department-self-service"
              v-model="form.enabledSelfService"
              type="checkbox"
              class="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            <label for="department-self-service" class="text-sm text-gray-700">
              Self service enabled
            </label>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              SNS topic ARN
            </label>
            <input
              v-model="form.snsTopicArn"
              type="text"
              maxlength="255"
              placeholder="arn:aws:sns:..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <!-- Infoauto server -->
        <h3 class="text-sm font-semibold text-gray-900 uppercase mb-3">
          Infoauto server
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Endpoint
            </label>
            <input
              v-model="form.serverEndpoint"
              type="text"
              maxlength="255"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Port
            </label>
            <input
              v-model="form.serverPort"
              type="number"
              min="0"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Database name
            </label>
            <input
              v-model="form.serverDbName"
              type="text"
              maxlength="255"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              v-model="form.serverUsername"
              type="text"
              maxlength="255"
              autocomplete="off"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              v-model="form.serverPassword"
              type="password"
              maxlength="255"
              autocomplete="new-password"
              :disabled="form.clearServerPassword"
              :placeholder="passwordPlaceholder"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            />
            <p class="mt-1 text-xs text-gray-500">
              The saved password is never sent back to the browser. Type a new
              one only if you want to replace it.
            </p>
            <div v-if="hasSavedPassword" class="mt-2 flex items-center gap-2">
              <input
                id="department-clear-password"
                v-model="form.clearServerPassword"
                type="checkbox"
                class="h-4 w-4 text-red-600 border-gray-300 rounded"
              />
              <label
                for="department-clear-password"
                class="text-sm text-gray-700"
              >
                Remove the saved password
              </label>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-3">
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {{ saving ? "Saving..." : isCreating ? "Create" : "Save changes" }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            @click="closeForm"
          >
            Cancel
          </button>
        </div>
      </form>
    </BaseModal>
  </div>
</template>

<script>
import {
  createDepartment,
  getDepartmentsForManagement,
  updateDepartment
} from "@/api/store/DepartmentsService.js";

export default {
  name: "DepartmentsManager",
  data() {
    return {
      departments: [],
      loading: false,
      saving: false,
      error: null,
      search: "",
      showForm: false,
      editingDepartment: null,
      form: this.emptyForm()
    };
  },
  computed: {
    isCreating() {
      return !this.editingDepartment;
    },
    hasSavedPassword() {
      return !!this.editingDepartment?.hasServerPassword;
    },
    passwordPlaceholder() {
      return this.hasSavedPassword
        ? "Leave blank to keep the saved password"
        : "No password saved";
    },
    filteredDepartments() {
      const term = this.search.trim().toLowerCase();
      if (!term) return this.departments;
      return this.departments.filter(department =>
        [
          department.nombre,
          department.codigo,
          department.departamentoId
        ].some(value => (value || "").toLowerCase().includes(term))
      );
    }
  },
  watch: {
    // El modal tambien se cierra con la X o clic fuera
    showForm(open) {
      if (!open) {
        this.editingDepartment = null;
        this.form = this.emptyForm();
      }
    }
  },
  mounted() {
    this.loadDepartments();
  },
  methods: {
    emptyForm() {
      return {
        ciaId: "",
        departamentoId: "",
        codigo: "",
        nombre: "",
        direccion: "",
        telefono: "",
        main: false,
        enabledSelfService: false,
        snsTopicArn: "",
        serverEndpoint: "",
        serverPort: "",
        serverDbName: "",
        serverUsername: "",
        serverPassword: "",
        clearServerPassword: false
      };
    },
    // tinyint llega como "1"/"0" desde la API de CodeIgniter
    isTrue(value) {
      return Number(value) === 1;
    },
    async loadDepartments() {
      this.loading = true;
      this.error = null;
      try {
        this.departments = await getDepartmentsForManagement();
      } catch (e) {
        console.error("Error loading departments", e);
        this.error = "Error loading departments";
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingDepartment = null;
      this.form = this.emptyForm();
      this.showForm = true;
    },
    openEdit(department) {
      this.editingDepartment = department;
      this.form = {
        ciaId: department.ciaId || "",
        departamentoId: department.departamentoId || "",
        codigo: department.codigo || "",
        nombre: department.nombre || "",
        direccion: department.direccion || "",
        telefono: department.telefono || "",
        main: this.isTrue(department.main),
        enabledSelfService: this.isTrue(department.enabledSelfService),
        snsTopicArn: department.snsTopicArn || "",
        serverEndpoint: department.serverEndpoint || "",
        serverPort:
          department.serverPort === null || department.serverPort === undefined
            ? ""
            : department.serverPort,
        serverDbName: department.serverDbName || "",
        serverUsername: department.serverUsername || "",
        serverPassword: "",
        clearServerPassword: false
      };
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
    },
    // La API espera snake_case (nombres de columna tal cual)
    buildPayload() {
      const payload = {
        nombre: this.form.nombre.trim(),
        direccion: this.form.direccion.trim(),
        telefono: this.form.telefono.trim(),
        main: this.form.main,
        enabled_self_service: this.form.enabledSelfService,
        sns_topic_arn: this.form.snsTopicArn.trim(),
        server_endpoint: this.form.serverEndpoint.trim(),
        server_port: this.form.serverPort,
        server_db_name: this.form.serverDbName.trim(),
        server_username: this.form.serverUsername.trim()
      };
      if (this.isCreating) {
        payload.cia_id = this.form.ciaId.trim();
        payload.departamento_id = this.form.departamentoId.trim();
        payload.codigo = this.form.codigo.trim();
      }
      if (this.form.clearServerPassword) {
        payload.clear_server_password = true;
      } else if (this.form.serverPassword) {
        payload.server_password = this.form.serverPassword;
      }
      return payload;
    },
    validateForm() {
      const required = [["Name", this.form.nombre]];
      if (this.isCreating) {
        required.push(
          ["Company id", this.form.ciaId],
          ["Department id", this.form.departamentoId],
          ["Code", this.form.codigo]
        );
      }
      const missing = required.find(([, value]) => !value.trim());
      if (missing) {
        this.$toast.open({
          message: `${missing[0]} is required`,
          type: "error",
          position: "top-right"
        });
        return false;
      }
      return true;
    },
    async submitForm() {
      if (!this.validateForm()) return;

      this.saving = true;
      const creating = this.isCreating;
      try {
        const payload = this.buildPayload();
        const saved = creating
          ? await createDepartment(payload)
          : await updateDepartment(this.editingDepartment.id, payload);

        if (creating) {
          this.departments.push(saved);
        } else {
          const index = this.departments.findIndex(d => d.id === saved.id);
          if (index !== -1) this.$set(this.departments, index, saved);
        }
        // Marcar uno como principal desactiva el anterior
        if (this.isTrue(saved.main)) {
          this.departments.forEach(d => {
            if (d.id !== saved.id) this.$set(d, "main", 0);
          });
        }
        this.closeForm();
        this.$toast.open({
          message: creating ? "Department created" : "Department updated",
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        console.error("Error saving department", e);
        this.$toast.open({
          message:
            e?.response?.data?.message ||
            (creating
              ? "Error creating the department"
              : "Error updating the department"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss">
.department-form-modal {
  .modal-content {
    max-width: 900px;
  }
  .content {
    padding: 20px;
  }
}
</style>
